import { SectionHeader, TextBadge } from '@/components/ui';
import { MaskedText } from '@/components/ui/MaskedText';
import { cn } from '@/lib/cn';
import { IconBuilding, IconDislike, IconLike, IconLocation, IconMapLocation } from '@repo/svgs';
import { CarouselClientOnly } from '@/components/carousel';
import { useMemo } from 'react';

interface RentValueSectionProps {
  buildingAddress: string;
  neighborhoodName?: string;
  boroughName?: string;
  isBlurRequired: boolean;
  rentValueData: any | null;
}

type MediansByBedrooms = Record<string, number | null | undefined>;

function toNumberOrNull(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function formatMoney(n: number | null): string {
  if (n === null) return '-';
  return `$${Math.round(n).toLocaleString()}`;
}

function getPercentage(a: number, b: number): number {
  // Matches your legacy behavior:
  // abs((b - a) / a) * 100, where callers pass `a` as the larger value.
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return 0;
  return Math.abs(((b - a) / a) * 100);
}

function formatPercent(p: number): string {
  const rounded = Math.round(p * 10) / 10;
  return rounded % 1 === 0 ? String(Math.trunc(rounded)) : String(rounded);
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function BedroomLabel(key: string): string {
  if (key === '0') return 'Studio';
  if (key === '1') return '1 bedroom';
  if (key === '2') return '2 bedrooms';
  return '3+ bedrooms';
}

function toCssSafeId(input: string): string {
  // Keep only [a-z0-9-] so we can safely use it in class selectors like `.${id}-track`.
  const slug = (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // collapse non-alnum to "-"
    .replace(/^-+|-+$/g, ''); // trim dashes
  return slug ? `c-${slug}` : 'c';
}

function BarRow({
  icon,
  label,
  value,
  widthPct,
  barClassName,
  isBlurRequired,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  widthPct: number;
  barClassName: string;
  isBlurRequired: boolean;
}) {
  return (
    <div className="grid grid-cols-[28px_1fr] gap-4">
      <div>{icon}</div>
      <div className="min-w-0">
        <div
          className={cn(
            'my-2 h-2 w-full rounded-full',
            isBlurRequired ? 'bg-[#64748B]' : value == null || value === 0 ? 'bg-secondary' : '',
          )}
        >
          {!isBlurRequired && value !== null ? (
            <div
              className={cn('h-2 rounded-full', barClassName)}
              style={{ width: `${Math.round(widthPct)}%` }}
            />
          ) : null}
        </div>
        <div>
          <div className="text-body-2-high text-primary">
            {isBlurRequired ? <MaskedText className="w-20 h-4" /> : formatMoney(value)}
          </div>
          <div className="text-caption text-secondary pt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

export function RentValueSection({
  buildingAddress,
  neighborhoodName = '',
  boroughName = '',
  isBlurRequired,
  rentValueData,
}: RentValueSectionProps) {
  const rv = rentValueData?.rent_value ?? rentValueData?.rentValue ?? null;

  const medianBuilding = toNumberOrNull(rv?.median_building_rent ?? rv?.medianBuildingRent);
  const medianNeighborhood = toNumberOrNull(
    rv?.median_neighborhood_rent ?? rv?.medianNeighborhoodRent,
  );
  const medianBorough = toNumberOrNull(rv?.median_borough_rent ?? rv?.medianBoroughRent);

  const buildingByBeds: MediansByBedrooms =
    rv?.building_medians_by_bedrooms ?? rv?.buildingMediansByBedrooms ?? {};
  const neighborhoodByBeds: MediansByBedrooms =
    rv?.neighborhood_medians_by_bedrooms ?? rv?.neighborhoodMediansByBedrooms ?? {};
  const boroughByBeds: MediansByBedrooms =
    rv?.borough_medians_by_bedrooms ?? rv?.boroughMediansByBedrooms ?? {};

  const showInsight = !isBlurRequired && medianBuilding !== null && medianNeighborhood !== null;
  const isEqual = showInsight ? medianBuilding === medianNeighborhood : false;
  const isBuildingHigher = showInsight ? medianBuilding > medianNeighborhood : false;

  // Use the larger value as denominator so % is symmetric (matches your screenshots).
  const pct = showInsight
    ? getPercentage(
        Math.max(medianBuilding, medianNeighborhood),
        Math.min(medianBuilding, medianNeighborhood),
      )
    : null;

  const insightText = showInsight
    ? isEqual
      ? `The median rent for all bedrooms at ${buildingAddress} is consistent with other buildings in ${neighborhoodName}.`
      : `The median rent for all bedrooms at ${buildingAddress} is ${formatPercent(pct ?? 0)}% ${
          isBuildingHigher ? 'more' : 'less'
        } than other buildings in ${neighborhoodName}.`
    : '';

  const topLeft = medianBuilding;
  const topRight = medianNeighborhood;

  const bedKeys = ['0', '1', '2', '3'];

  const bedroomCardNodes = useMemo(() => {
    return bedKeys.map(key => {
      const b = toNumberOrNull(buildingByBeds?.[key]);
      const n = toNumberOrNull(neighborhoodByBeds?.[key]);
      const bo = toNumberOrNull(boroughByBeds?.[key]);
      const max = Math.max(b ?? 0, n ?? 0, bo ?? 0);
      const bW = max > 0 ? clamp01((b ?? 0) / max) * 100 : 0;
      const nW = max > 0 ? clamp01((n ?? 0) / max) * 100 : 0;
      const boW = max > 0 ? clamp01((bo ?? 0) / max) * 100 : 0;

      return {
        id: key,
        content: (
          <div className="rounded-2xl border border-secondary bg-primary p-4">
            <div className="text-body-1-high text-primary">{BedroomLabel(key)}</div>

            <div className="mt-4 space-y-4">
              <BarRow
                icon={<IconBuilding className="w-6 h-6 icon-secondary" aria-hidden />}
                label={buildingAddress}
                value={b}
                widthPct={bW}
                barClassName="bg-accent"
                isBlurRequired={isBlurRequired}
              />
              <BarRow
                icon={<IconLocation className="w-6 h-6 icon-secondary" aria-hidden />}
                label={neighborhoodName}
                value={n}
                widthPct={nW}
                barClassName="bg-[#3B2AD8]"
                isBlurRequired={isBlurRequired}
              />
              <BarRow
                icon={<IconMapLocation className="w-6 h-6 icon-secondary" aria-hidden />}
                label={boroughName}
                value={bo}
                widthPct={boW}
                barClassName="bg-[#2B2689]"
                isBlurRequired={isBlurRequired}
              />
            </div>
          </div>
        ),
      };
    });
  }, [
    bedKeys,
    boroughName,
    boroughByBeds,
    buildingAddress,
    buildingByBeds,
    isBlurRequired,
    neighborhoodByBeds,
    neighborhoodName,
  ]);

  const bedroomCarouselId = useMemo(
    () => `rent-value-beds-${toCssSafeId(buildingAddress)}`,
    [buildingAddress],
  );

  return (
    <div className="w-full">
      <SectionHeader
        title={`Rent value at ${buildingAddress}`}
        subtitleText="Based on Openigloo inventory from past 3 years"
      />

      <div className="md:px-4 px-2">
        {/* Insight badge */}
        {isBlurRequired ? (
          <div className="mt-2 rounded-2xl bg-secondary p-4">
            <MaskedText className="h-4 w-3/4" />
          </div>
        ) : showInsight ? (
          <TextBadge
            // Match designs: full-width pill, larger padding/radius, more icon/text spacing.
            className="mt-2 w-full !rounded-2xl !px-4 !py-3 !gap-3"
            variant={isBuildingHigher ? 'negative' : 'positive'}
            icon={
              isBuildingHigher ? (
                <IconDislike className="w-4 h-4 icon-alert shrink-0" aria-hidden />
              ) : (
                <IconLike className="w-4 h-4 icon-success shrink-0" aria-hidden />
              )
            }
          >
            {insightText}
          </TextBadge>
        ) : null}

        {/* Top medians */}
        <div className="mt-4 grid grid-cols-2">
          <div className="pr-4">
            <div className="text-caption text-secondary">{buildingAddress}</div>
            <div className="mt-1 text-heading-6 text-primary">
              {isBlurRequired ? <MaskedText className="w-16 h-5" /> : formatMoney(topLeft)}
            </div>
          </div>
          <div className="border-l border-tertiary pl-4">
            <div className="text-caption text-secondary">{neighborhoodName}</div>
            <div className="mt-1 text-heading-6 text-primary">
              {isBlurRequired ? <MaskedText className="w-16 h-5" /> : formatMoney(topRight)}
            </div>
          </div>
        </div>

        {/* Bedroom cards */}
        <div className="mt-4">
          {/* Mobile: carousel (match complaint history breakpoints/layout) */}
          <div className="sm:hidden">
            <CarouselClientOnly
              className="col-span-full -mx-6"
              carouselId={bedroomCarouselId}
              items={bedroomCardNodes}
              perView={{ mobile: 1, tablet: 1, desktop: 1 }}
              peek={{ mobile: 12, tablet: 0, desktop: 0 }}
              padStart={{ mobile: 24, tablet: 0, desktop: 0 }}
              padEnd={{ mobile: 24, tablet: 0, desktop: 0 }}
              gap={16}
              ariaLabel="Rent value by bedroom"
            />
          </div>

          {/* Larger screens: grid */}
          <div className="hidden sm:grid md:grid-cols-2 gap-4">
            {bedroomCardNodes.map(item => (
              <div key={item.id}>{item.content}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
